import React, { useEffect, useRef, useState } from 'react';
import { ButtonIcon, Icon, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { useIspmContext } from '../../../context';
import { useChatOverlayContext } from '../../../context/chatOverlayContext';
import { useSaviChatAskApi } from '../../../hooks';
import { useInvertedLocalize } from '../../../utils/localization';
import { invertedMessages } from '../../../utils/messages';

import './SaviChatInput.css';

const MAX_INPUT_LENGTH = 500;
const MAX_HEIGHT_PX = 116; // This is the Maximum height in pixels
const PIXELS_PER_REM =
  parseFloat(getComputedStyle(document.documentElement).fontSize) || 16; // This is converting from pixels to rem
const MIN_SCROLL_HEIGHT = 48; // This is Minimum scroll height to display text count

function SaviChatInput() {
  const [isTextCountVisible, setisTextCountVisible] = useState(false);
  const { chatOverlayState, updateChatOverlayState } = useChatOverlayContext();
  const { ispmState } = useIspmContext();
  const l = useInvertedLocalize(invertedMessages);

  const { handleChatAskApiInChatOverlay } = useSaviChatAskApi();

  const saviChatInputRef = useRef<HTMLInputElement>(null);
  const saviChatInputTextSize = chatOverlayState.saviInputText.trim().length;

  useEffect(() => {
    if (!saviChatInputRef.current) {
      return;
    }

    saviChatInputRef.current.style.height = '0px';

    const { scrollHeight } = saviChatInputRef.current;

    if (scrollHeight > MIN_SCROLL_HEIGHT) {
      setisTextCountVisible(true);
    } else {
      setisTextCountVisible(false);
    }

    saviChatInputRef.current.style.height = `${
      Math.min(scrollHeight, MAX_HEIGHT_PX) / PIXELS_PER_REM
    }rem`;
  }, [chatOverlayState.saviInputText]);

  const setOverlayInputValue = (inputText: string) => {
    updateChatOverlayState('saviInputText', inputText);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (saviChatInputTextSize > MAX_INPUT_LENGTH) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (chatOverlayState.isApiCallInProgress) {
        updateChatOverlayState('isWarningModalOpen', true);
      } else {
        handleChatAskApiInChatOverlay({
          chatuuid: ispmState.chatuuid,
          freeText: chatOverlayState.saviInputText,
          chartType: 'vegalite',
        });
      }
    }
  };

  const handleSuffixIconClick = () => {
    if (saviChatInputTextSize > MAX_INPUT_LENGTH) {
      return;
    }

    if (chatOverlayState.isApiCallInProgress) {
      updateChatOverlayState('isWarningModalOpen', true);
    } else {
      handleChatAskApiInChatOverlay({
        chatuuid: ispmState.chatuuid,
        freeText: chatOverlayState.saviInputText,
      });
    }
  };

  return (
    <>
      <div
        className={cx(
          chatOverlayState.showGeneratedContent
            ? 'Ispm-SaviChatInput--small'
            : 'Ispm-SaviChatInput--large',
          isTextCountVisible && 'Ispm-SaviChatInput--hasCount'
        )}>
        <div className='Ispm-SaviInputField-prefixIcon'>
          <i>
            <Icon kind='Comment' color='purple-700' size='smallMedium' />
          </i>
        </div>
        <div className='Ispm-SaviInputfieldMain'>
          <InputField
            className='Ispm-SaviInputfield'
            kind='multiline'
            name='chat-input'
            placeholder={l('What can I help you with?')}
            isResetButtonVisible={false}
            value={chatOverlayState.saviInputText}
            setValue={setOverlayInputValue}
            onKeyDown={handleKeyDown}
            isDisabled={chatOverlayState.inputDisabled}
            ref={saviChatInputRef}
          />
          {isTextCountVisible && (
            <div
              className={
                saviChatInputTextSize <= MAX_INPUT_LENGTH
                  ? 'Ispm-SaviChatInput-saviTextCount'
                  : 'Ispm-SaviChatInput-saviTextCount--isDisabled'
              }>
              {saviChatInputTextSize}/{MAX_INPUT_LENGTH}
            </div>
          )}
        </div>
        <div className='Ispm-SaviInputField-suffixButton'>
          <ButtonIcon
            size='medium'
            kind='saviGhost'
            isDisabled={
              saviChatInputTextSize > MAX_INPUT_LENGTH ||
              saviChatInputTextSize === 0
            }
            icon={
              <Icon
                kind={chatOverlayState.isApiCallInProgress ? 'Stop' : 'ArrowUp'}
                className={
                  saviChatInputTextSize > MAX_INPUT_LENGTH
                    ? 'Ispm-saviSuffixButtonIcon--isDisabled'
                    : ''
                }
                size='small'
              />
            }
            onClick={handleSuffixIconClick}
          />
        </div>
      </div>

      <div className='Ispm-SaviChatInput-caption'>
        {l('Savi is always learning new things. Results may vary.')}
      </div>
    </>
  );
}

export default SaviChatInput;
